import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, StickyNote, Search, Trash2, Edit } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const NotesPage = () => {
    const { api } = useAuth();
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingNote, setEditingNote] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        content: ''
    });

    const fetchNotes = async () => {
        try {
            const response = await api.get('/notes');
            setNotes(response.data);
        } catch (error) {
            toast.error('Fehler beim Laden');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNotes();
    }, []);

    const handleSave = async () => {
        try {
            if (editingNote) {
                await api.put(`/notes/${editingNote.id}`, formData);
                toast.success('Notiz aktualisiert');
            } else {
                await api.post('/notes', formData);
                toast.success('Notiz erstellt');
            }
            setShowModal(false);
            setEditingNote(null);
            resetForm();
            fetchNotes();
        } catch (error) {
            toast.error('Fehler beim Speichern');
        }
    };

    const handleDelete = async (noteId) => {
        if (!window.confirm('Notiz wirklich löschen?')) return;
        try {
            await api.delete(`/notes/${noteId}`);
            toast.success('Notiz gelöscht');
            fetchNotes();
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
    };

    const openEdit = (note) => {
        setEditingNote(note);
        setFormData({
            title: note.title,
            content: note.content
        });
        setShowModal(true);
    };

    const resetForm = () => {
        setFormData({
            title: '',
            content: ''
        });
    };

    const filteredNotes = notes.filter(n => 
        n.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        n.content.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Sticky note colors
    const noteColors = [
        'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800',
        'bg-pink-100 dark:bg-pink-900/30 border-pink-200 dark:border-pink-800',
        'bg-blue-100 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800',
        'bg-green-100 dark:bg-green-900/30 border-green-200 dark:border-green-800',
        'bg-purple-100 dark:bg-purple-900/30 border-purple-200 dark:border-purple-800',
    ];

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="p-6 md:p-8" data-testid="notes-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        📝 Notizen
                    </h1>
                    <p className="text-muted-foreground">Private Schnellnotizen</p>
                </div>
                <Button onClick={() => { resetForm(); setEditingNote(null); setShowModal(true); }} data-testid="add-note-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Neue Notiz
                </Button>
            </div>

            {/* Search */}
            <div className="relative mb-6">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                    placeholder="Notizen suchen..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="note-search"
                />
            </div>

            {/* Notes Grid - Masonry-like */}
            <div className="columns-1 md:columns-2 lg:columns-3 gap-4 space-y-4">
                {filteredNotes.map((note, index) => (
                    <motion.div
                        key={note.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="break-inside-avoid"
                    >
                        <Card 
                            className={`shadow-soft ${noteColors[index % noteColors.length]} border-2 cursor-pointer hover:shadow-floating transition-all`}
                            onClick={() => openEdit(note)}
                            data-testid={`note-card-${note.id}`}
                        >
                            <CardContent className="pt-4">
                                <div className="flex items-start justify-between mb-2">
                                    <h3 className="font-semibold text-lg">{note.title}</h3>
                                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                                        <Button variant="ghost" size="icon" onClick={() => openEdit(note)} data-testid={`edit-note-${note.id}`}>
                                            <Edit className="w-4 h-4" />
                                        </Button>
                                        <Button variant="ghost" size="icon" onClick={() => handleDelete(note.id)} data-testid={`delete-note-${note.id}`}>
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                                <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                                <p className="text-xs text-muted-foreground mt-3">
                                    {format(new Date(note.updated_at), 'd. MMM yyyy, HH:mm', { locale: de })}
                                </p>
                            </CardContent>
                        </Card>
                    </motion.div>
                ))}

                {filteredNotes.length === 0 && (
                    <div className="col-span-full text-center py-12 text-muted-foreground">
                        <StickyNote className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>Keine Notizen vorhanden</p>
                    </div>
                )}
            </div>

            {/* Modal */}
            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="max-w-lg" data-testid="note-modal">
                    <DialogHeader>
                        <DialogTitle>{editingNote ? 'Notiz bearbeiten' : 'Neue Notiz'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Input
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                placeholder="Titel"
                                className="text-lg font-semibold border-none shadow-none focus-visible:ring-0 px-0"
                                data-testid="note-title-input"
                            />
                        </div>
                        <div>
                            <Textarea
                                value={formData.content}
                                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                placeholder="Schreibe hier deine Notiz..."
                                className="min-h-[200px] resize-none border-none shadow-none focus-visible:ring-0 px-0"
                                data-testid="note-content-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowModal(false)}>Abbrechen</Button>
                        <Button onClick={handleSave} disabled={!formData.title || !formData.content} data-testid="save-note-button">
                            {editingNote ? 'Aktualisieren' : 'Speichern'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default NotesPage;
